import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from '../../common/Toast/toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { confirm } from '../../common/Confirm/confirm';
import { updateBatch } from '../../../../services/BatchService.js';
import NewCustomDataTable from '../../common/NewCustomDataTable.jsx';
import { getStudentBatchReport, getBatchReportSummary, getDuplicateBatches } from '../../../../services/BatchReportService';
import { getDepartments } from '../../../../services/DepartmentService';
import { getBranches } from '../../../../services/BranchService';
import { getcourses } from '../../../../services/CourseModule';
import SearchablePagedSelect from './SearchablePagedSelect.jsx';
import BatchMixChart from './BatchMixChart.jsx';
import '../../Trainer/TrainerDashboard.css';
import '../../common/lms-pages.css';
import FilterSheet from '../../common/FilterSheet/FilterSheet';
import useDebouncedCallback from '../../common/useDebouncedCallback';
import AddBatch from '../../Forms/BatchForm/AddBatch.jsx';
import BatchStudentsManager from '../../Forms/BatchForm/BatchStudentsManager.jsx';
import PopupDone, { usePopupDone } from '../../common/PopupDone/PopupDone.jsx';

// Label above the figure, not below: a caption under an oversized number
// reads as a poster; above it, it reads as a report. The tone is a 2px rule
// on the top edge, never a fill behind the number.
const StatCard = ({ label, value, tone = "" }) => (
  <div className={`br-stat ${tone}`.trim()}>
    <span className="br-stat-label">{label}</span>
    <span className="br-stat-value">{value}</span>
  </div>
);

const formatDate = (v) => (v ? new Date(v).toLocaleDateString() : '—');

const BatchReportPage = () => {
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const { hasPermission, user: authUser } = useAuth();

  useEffect(() => {
    if (!hasPermission('batchreport', 'view', 'lms')) {
      if (!hasShownToast.current) {
        toast.error('You do not have permission to view this report.');
        navigate('/');
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // --- Filters ---
  const [departmentId, setDepartmentId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [batchStatus, setBatchStatus] = useState('');
  const [onlyMultiple, setOnlyMultiple] = useState(false);
  const [search, setSearch] = useState('');

  // --- Summary stat cards ---
  const [summary, setSummary] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const data = await getBatchReportSummary();
      setSummary(data);
    } catch (err) {
      console.error('Failed to load batch report summary:', err);
    }
  }, []);

  // --- Student list ---
  // Server-side: a page of rows at a time, appended as the sentinel under
  // the table comes into view. Filter changes (dropdowns/checkbox/search
  // text) are staged and only take effect on Apply, which resets to page
  // one because the result set is a different one.
  const [students, setStudents] = useState([]);
  const [studentsTotal, setStudentsTotal] = useState(0);
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsLimit, setStudentsLimit] = useState(25);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsHasMore, setStudentsHasMore] = useState(false);
  const [loadingMoreStudents, setLoadingMoreStudents] = useState(false);

  // appendRef is read at the top of the loader, before any await, because
  // by the time the request resolves the next scroll may already have
  // flipped it. loadingMoreRef keeps one page in flight at a time.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const studentsEndRef = useRef(null);

  // `overrides` exists for Reset: setState is asynchronous, so a reset that
  // cleared the six filters and then called this would still send the old
  // ones. It passes the cleared set in directly instead.
  const loadStudents = useCallback(async (page, limit, overrides) => {
    const f = {
      search,
      departmentId,
      courseId,
      branchId,
      batchStatus,
      onlyMultiple,
      ...(overrides || {}),
    };
    const appending = appendRef.current;
    if (appending) setLoadingMoreStudents(true);
    else setStudentsLoading(true);
    try {
      const { data, pagination } = await getStudentBatchReport({
        page,
        limit,
        search: f.search || undefined,
        departmentId: f.departmentId || undefined,
        courseId: f.courseId || undefined,
        branchId: f.branchId || undefined,
        batchStatus: f.batchStatus || undefined,
        minBatches: f.onlyMultiple ? 2 : undefined,
      });
      // The table sorts on `row[header.prop]`, not on the rendered cell, so
      // a computed column needs its value precomputed onto the row itself.
      const rows = (Array.isArray(data) ? data : []).map((row) => ({
        ...row,
        departments: [...new Set((row.batches || []).map((b) => b.departmentName).filter(Boolean))].join(', '),
      }));
      const total = pagination?.total || 0;
      setStudents((prev) => {
        if (!appending) return rows;
        const seen = new Set(prev.map((r) => r.studentId));
        return [...prev, ...rows.filter((r) => !seen.has(r.studentId))];
      });
      setStudentsTotal(total);
      setStudentsHasMore(page * limit < total);
    } catch (err) {
      console.error('Failed to load batch report:', err);
      toast.error('Could not load batch report.');
    } finally {
      // Every path resets these, or the next scroll is ignored forever.
      appendRef.current = false;
      loadingMoreRef.current = false;
      setLoadingMoreStudents(false);
      setStudentsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, departmentId, courseId, branchId, batchStatus, onlyMultiple]);

  // Infinite scroll: the sentinel under the table asks for the next page
  // when it comes within 150px of the viewport.
  useEffect(() => {
    const el = studentsEndRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        if (loadingMoreRef.current || studentsLoading || !studentsHasMore) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        const next = studentsPage + 1;
        setStudentsPage(next);
        loadStudents(next, studentsLimit);
      },
      { rootMargin: '150px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [studentsHasMore, studentsLoading, studentsPage, studentsLimit, loadStudents]);

  useEffect(() => {
    if (!hasPermission('batchreport', 'view', 'lms')) return;
    loadSummary();
    loadStudents(1, studentsLimit);
    loadDuplicates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* The search box does not wait for the Search button -- it runs itself
     once the typing stops. The five pickers beside it still wait, because
     choosing three of those should cost one refetch, not three.

     The term goes in as an override rather than being read back off
     state: setSearch has not necessarily landed by the time this runs,
     which is the case `overrides` was added for. */
  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setStudentsPage(1);
    loadStudents(1, studentsLimit, { search: value });
  });

  // The dropdowns and the checkbox are staged in state and take effect
  // when the user clicks Search / presses Enter — back to page 1, since
  // the result set is a different one now.
  const handleSearch = () => {
    /* This sends the search itself, so a keystroke still waiting out its
       delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setStudentsPage(1);
    loadStudents(1, studentsLimit);
  };

  // A new batch size is a fresh list from the top -- the rows on screen
  // were cut at the old boundary.
  const handleStudentsLimitChange = (value) => {
    const next = Number(value);
    runSearch.cancel();
    appendRef.current = false;
    setStudentsLimit(next);
    setStudentsPage(1);
    loadStudents(1, next);
  };

  // How many of the six are on. It drives the count beside the buttons and
  // whether Reset is live, so the strip says what it is doing.
  const filterCount =
    (departmentId ? 1 : 0) +
    (courseId ? 1 : 0) +
    (branchId ? 1 : 0) +
    (batchStatus ? 1 : 0) +
    (onlyMultiple ? 1 : 0) +
    (search.trim() ? 1 : 0);

  const handleResetFilters = () => {
    runSearch.cancel();
    const cleared = {
      search: '',
      departmentId: '',
      courseId: '',
      branchId: '',
      batchStatus: '',
      onlyMultiple: false,
    };
    setSearch('');
    setDepartmentId('');
    setCourseId('');
    setBranchId('');
    setBatchStatus('');
    setOnlyMultiple(false);
    appendRef.current = false;
    setStudentsPage(1);
    loadStudents(1, studentsLimit, cleared);
  };

  // --- Duplicate batches (same course + trainer + name) ---
  const [duplicates, setDuplicates] = useState(null);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);

  const loadDuplicates = useCallback(async () => {
    setDuplicatesLoading(true);
    try {
      const result = await getDuplicateBatches();
      setDuplicates(result?.duplicateGroups || []);
    } catch (err) {
      console.error('Failed to load duplicate batches:', err);
      toast.error('Could not load duplicate batches.');
    } finally {
      setDuplicatesLoading(false);
    }
  }, []);

  // --- Row detail modal ---
  const [selectedStudent, setSelectedStudent] = useState(null);
  // A change saved in the popup -- at either step -- is confirmed in it
  // (the done panel), which then closes the popup by itself.
  const { done: studentDone } = usePopupDone(Boolean(selectedStudent));

  /* Manage, in two steps of the same popup. Step 1 is the student's batch
     list, where one batch is picked; Manage takes the popup to step 2, the
     picked batch, with its edit form and its roster as two tabs. Only for
     someone who may update batches -- without that permission nothing is
     pickable and Manage is not drawn. */
  const canManage = hasPermission('batches', 'update', 'lms');
  /* A SET, not one id: a student who was put in the wrong course is
     usually in several of its batches, and taking them out one popup at a
     time is the same confirmation answered over and over. Manage still
     wants exactly one batch, so it appears only when one is picked. */
  const [pickedBatchIds, setPickedBatchIds] = useState(() => new Set());
  const [manageStep, setManageStep] = useState(1);
  const [manageTab, setManageTab] = useState('edit');
  const pickedBatches = useMemo(
    () => (selectedStudent?.batches || []).filter((b) => pickedBatchIds.has(b.batchId)),
    [selectedStudent, pickedBatchIds]
  );
  // Manage edits one batch; it is drawn only when one is picked.
  const pickedBatch = pickedBatches.length === 1 ? pickedBatches[0] : null;

  const togglePicked = (batchId) =>
    setPickedBatchIds((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });

  /* Take this student out of the picked batch, from here.

     It was three steps and a detour: Manage, the Students tab, find the
     name in a forty-row roster, mark it, Save. The report already knows
     both ids, so the only thing missing was asking.

     The row picks the batch and the footer acts on it -- the same
     contract the popup's table already has -- and the ask is the house
     confirm, in its danger tone, because this is not undone by pressing
     the button again. `removeUserIds` alone is enough: the API takes the
     student out of the batch AND the batch out of the student, and tells
     them so (batch.service.js update). */
  const [removingFromBatch, setRemovingFromBatch] = useState(false);

  const removeStudentFromBatch = async () => {
    if (!selectedStudent || pickedBatches.length === 0 || removingFromBatch) return;
    const studentName = selectedStudent.studentName || 'this student';
    const names = pickedBatches.map((b) => b.batchName || 'Unnamed batch');
    const many = pickedBatches.length > 1;

    const answer = await confirm({
      title: many
        ? `Remove ${studentName} from ${pickedBatches.length} batches?`
        : `Remove ${studentName} from ${names[0]}?`,
      // The batches are named, because "3 batches" is not something anyone
      // can check before answering.
      text: `${many ? `${names.join(', ')}. ` : ''}They stop seeing ${
        many ? 'these batches, their classes and chat groups' : 'this batch, its classes and its chat group'
      }. The ${many ? 'batches are' : 'batch itself is'} not changed, and they can be added back.`,
      confirmButtonText: many ? `Remove from ${pickedBatches.length}` : 'Remove',
      tone: 'danger',
    });
    if (!answer.isConfirmed) return;

    setRemovingFromBatch(true);
    const studentId = String(selectedStudent.studentId || '');
    try {
      /* One PUT per batch, because a roster belongs to its batch -- there
         is no endpoint that takes a student out of several at once. They
         go together and each is settled on its own, so one batch refusing
         does not undo the others: what went through is reported as what
         went through. */
      const results = await Promise.allSettled(
        pickedBatches.map((b) =>
          updateBatch(b.batchId, { removeUserIds: studentId, updatedBy: authUser?.id || 'NA' })
        )
      );
      const removed = pickedBatches.filter((_b, i) => results[i].status === 'fulfilled');
      const failed = pickedBatches.filter((_b, i) => results[i].status === 'rejected');

      if (removed.length) {
        const removedIds = new Set(removed.map((b) => b.batchId));
        // The popup is reading a list it already holds, so it drops the
        // rows rather than waiting for the report behind it to come back.
        setSelectedStudent((prev) =>
          prev
            ? { ...prev, batches: (prev.batches || []).filter((b) => !removedIds.has(b.batchId)) }
            : prev
        );
        setPickedBatchIds((prev) => new Set([...prev].filter((id) => !removedIds.has(id))));
        refreshReport();
      }

      if (!failed.length) {
        toast.success(
          removed.length > 1
            ? `${studentName} was removed from ${removed.length} batches`
            : `${studentName} was removed from ${removed[0]?.batchName || 'the batch'}`
        );
      } else if (removed.length) {
        toast.error(
          `Removed from ${removed.length} of ${pickedBatches.length}. ${failed
            .map((b) => b.batchName || 'a batch')
            .join(', ')} could not be changed.`
        );
      } else {
        toast.error('Could not remove the student. Nothing was changed.');
      }
    } finally {
      setRemovingFromBatch(false);
    }
  };

  const closeStudentDetail = () => {
    setSelectedStudent(null);
    setPickedBatchIds(new Set());
    setManageStep(1);
    setManageTab('edit');
  };

  // A saved change can move a student between batches or rename one, so
  // the report the popup was opened from is asked again.
  const refreshReport = () => {
    appendRef.current = false;
    setStudentsPage(1);
    loadStudents(1, studentsLimit);
    loadDuplicates();
    loadSummary();
  };

  // Same four filter dimensions as the main page, but applied locally to
  // this one student's already-loaded batch list — no extra API call.
  // Staged (modalXxx) vs applied (modalAppliedFilters) mirrors the main
  // page's "only filter on Search click" behavior.
  const [modalDeptId, setModalDeptId] = useState('');
  const [modalCourseId, setModalCourseId] = useState('');
  const [modalBranchId, setModalBranchId] = useState('');
  const [modalStatus, setModalStatus] = useState('');
  const [modalAppliedFilters, setModalAppliedFilters] = useState({
    deptId: '',
    courseId: '',
    branchId: '',
    status: '',
  });

  const openStudentDetail = (row) => {
    setSelectedStudent(row);
    setPickedBatchIds(new Set());
    setManageStep(1);
    setManageTab('edit');
    setModalDeptId('');
    setModalCourseId('');
    setModalBranchId('');
    setModalStatus('');
    setModalAppliedFilters({ deptId: '', courseId: '', branchId: '', status: '' });
  };

  const handleModalSearch = () => {
    setModalAppliedFilters({
      deptId: modalDeptId,
      courseId: modalCourseId,
      branchId: modalBranchId,
      status: modalStatus,
    });
  };

  const handleModalReset = () => {
    setModalDeptId('');
    setModalCourseId('');
    setModalBranchId('');
    setModalStatus('');
    setModalAppliedFilters({ deptId: '', courseId: '', branchId: '', status: '' });
  };

  // Escape closes the dialog. The backdrop already did; a dialog that
  // only closes by mouse is the one thing every other popup here does not
  // do.
  useEffect(() => {
    if (!selectedStudent) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeStudentDetail();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedStudent]);

  const uniqueBy = (items, idKey, nameKey) => {
    const seen = new Map();
    (items || []).forEach((item) => {
      if (item[idKey] && !seen.has(item[idKey])) seen.set(item[idKey], item[nameKey]);
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  };

  const modalDeptOptions = useMemo(
    () => uniqueBy(selectedStudent?.batches, 'departmentId', 'departmentName'),
    [selectedStudent]
  );
  const modalCourseOptions = useMemo(
    () => uniqueBy(selectedStudent?.batches, 'courseId', 'courseName'),
    [selectedStudent]
  );
  const modalBranchOptions = useMemo(
    () => uniqueBy(selectedStudent?.batches, 'branchId', 'branchName'),
    [selectedStudent]
  );

  const filteredModalBatches = useMemo(() => {
    const all = selectedStudent?.batches || [];
    return all.filter(
      (b) =>
        (!modalAppliedFilters.deptId || b.departmentId === modalAppliedFilters.deptId) &&
        (!modalAppliedFilters.courseId || b.courseId === modalAppliedFilters.courseId) &&
        (!modalAppliedFilters.branchId || b.branchId === modalAppliedFilters.branchId) &&
        (!modalAppliedFilters.status || b.status === modalAppliedFilters.status)
    );
  }, [selectedStudent, modalAppliedFilters]);

  /* The head checkbox works on what the filters left, never on the whole
     list: ticking a box that also picks rows nobody can see is how a
     removal takes batches its reader never agreed to. */
  const allFilteredPicked =
    filteredModalBatches.length > 0 && filteredModalBatches.every((b) => pickedBatchIds.has(b.batchId));
  const somePicked = filteredModalBatches.some((b) => pickedBatchIds.has(b.batchId));

  const toggleAllFiltered = () =>
    setPickedBatchIds((prev) => {
      const next = new Set(prev);
      if (allFilteredPicked) filteredModalBatches.forEach((b) => next.delete(b.batchId));
      else filteredModalBatches.forEach((b) => next.add(b.batchId));
      return next;
    });

  // The readout at the top of the dialog: the figures the page reports,
  // narrowed to this one student. Over the whole batch list, not the
  // filtered one -- a filter should not change what the student is.
  const modalTally = useMemo(() => {
    const all = selectedStudent?.batches || [];
    return {
      total: all.length,
      active: all.filter((b) => b.status !== 'ended').length,
      ended: all.filter((b) => b.status === 'ended').length,
      departments: uniqueBy(all, 'departmentId', 'departmentName').length,
    };
  }, [selectedStudent]);

  // Applied drives the empty state's wording; dirty drives Reset, so the
  // button is live as soon as a picker moves rather than only after Apply.
  const modalFiltersApplied = Object.values(modalAppliedFilters).some(Boolean);
  const modalFiltersDirty =
    modalFiltersApplied || !!(modalDeptId || modalCourseId || modalBranchId || modalStatus);

  const studentHeaders = useMemo(
    () => [
      {
        title: 'Student',
        prop: 'studentName',
        cell: (row) => (
          <div className="lp-cell">
            <span className="lp-name">{row.studentName || 'Unnamed'}</span>
            <span className="lp-sub">
              {row.batchCount} batch{row.batchCount === 1 ? '' : 'es'}
            </span>
          </div>
        ),
      },
      {
        title: 'Email',
        prop: 'studentEmail',
        cell: (row) => (
          <span className="lp-contact">{row.studentEmail || '—'}</span>
        ),
      },
      {
        // The point of the report: one batch is ordinary, more is worth
        // seeing, so only the second case gets a tone.
        title: 'Batches',
        prop: 'batchCount',
        cell: (row) =>
          row.batchCount > 1 ? (
            <span className="pg-pill pg-pill--warn">{row.batchCount}</span>
          ) : (
            <span className="lp-num">{row.batchCount}</span>
          ),
      },
      {
        title: 'Departments',
        prop: 'departments',
        cell: (row) => {
          const list = (row.departments || '').split(', ').filter(Boolean);
          if (list.length === 0) return <span className="lp-muted">—</span>;
          return (
            <span className="lp-plain" title={list.join(', ')}>
              {list.slice(0, 2).join(', ')}
              {list.length > 2 && (
                <span className="lp-more">+{list.length - 2} more</span>
              )}
            </span>
          );
        },
      },
    ],
    []
  );

  if (!hasPermission('batchreport', 'view', 'lms')) {
    return null;
  }

  return (
    <div className="pg lp">

      <header className="pg-head">
        <div className="pg-head-main">
          <h4 className="pg-title">Batch report</h4>
        </div>
      </header>

      {/* The readout: five figures, label above each. */}
      {summary && (
        <div className="br-stats">
          <div>
            <StatCard label="Total students" value={summary.totals.totalStudents} />
          </div>
          <div>
            <StatCard label="Total batches" value={summary.totals.totalBatches} />
          </div>
          <div>
            <StatCard label="Active batches" value={summary.totals.activeBatches} tone="br-stat--good" />
          </div>
          <div>
            <StatCard label="Ended batches" value={summary.totals.endedBatches} tone="br-stat--bad" />
          </div>
          <div>
            <StatCard
              label="In multiple batches"
              value={summary.totals.studentsWithMultipleBatches}
              tone="br-stat--warn"
            />
          </div>
        </div>
      )}

      {summary && <BatchMixChart summary={summary} />}

      {/* ── Filters ───────────────────────────────────────────────────────
          Two rows that now share one grid, so the search box lines up under
          the pickers instead of running to its own width. The second row
          used .pg-filters-fields--notrainer, a three-column template written
          for another page's trainer/batch/status strip, to hold two things.

          Nothing applies until Apply, which is why the strip says how many
          filters are on and offers a way to put them all back. */}
      <FilterSheet className="br-filters" label="Filter the report">
        <div className="pg-filters-fields br-fields">
          <SearchablePagedSelect
            label="Department"
            value={departmentId}
            onChange={setDepartmentId}
            fetchPage={(page, term) => getDepartments(10, page, term)}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.id}
            allLabel="All departments"
            searchPlaceholder="Search departments…"
          />
          <SearchablePagedSelect
            label="Course"
            value={courseId}
            onChange={setCourseId}
            fetchPage={(page, term) => getcourses(10, page, term)}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.id}
            allLabel="All courses"
            searchPlaceholder="Search courses…"
          />
          <SearchablePagedSelect
            label="Branch"
            value={branchId}
            onChange={setBranchId}
            fetchPage={(page, term) => getBranches(10, page, term, true)}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.id}
            allLabel="All branches"
            searchPlaceholder="Search branches…"
          />
          <div className="pg-field">
            <label className="pg-label" htmlFor="br-status">
              Batch status
            </label>
            <select
              id="br-status"
              className="pg-select"
              value={batchStatus}
              onChange={(e) => setBatchStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="ended">Ended</option>
            </select>
          </div>
        </div>

        <div className="br-filters-row">
          <div className="pg-field br-filters-search">
            <label className="pg-label" htmlFor="br-search">
              Search
            </label>
            <input
              id="br-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                runSearch(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="br-rows">
              Rows per load
            </label>
            <select
              id="br-rows"
              className="pg-select"
              value={studentsLimit}
              onChange={(e) => handleStudentsLimitChange(e.target.value)}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {/* The report's own question, so it sits with the filters rather
              than in the actions cluster. */}
          <label className="br-check br-filters-check" htmlFor="only-multiple-batches">
            <input
              id="only-multiple-batches"
              type="checkbox"
              checked={onlyMultiple}
              onChange={(e) => setOnlyMultiple(e.target.checked)}
            />
            Only students in 2+ batches
          </label>

          <div className="pg-filters-actions br-filters-acts">
            {filterCount > 0 && (
              <span className="br-filters-count">
                {filterCount} filter{filterCount === 1 ? '' : 's'}
              </span>
            )}
            <button
              type="button"
              className="pg-btn"
              onClick={handleResetFilters}
              disabled={filterCount === 0}
            >
              Reset
            </button>
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={handleSearch}
            >
              Apply
            </button>
          </div>
        </div>
      </FilterSheet>

      <div className="td-list td-list--lms">
        <NewCustomDataTable
          data={students}
          headers={studentHeaders}
          totalRecords={studentsTotal}
          currentPage={studentsPage}
          recordsPerPage={studentsLimit}
          onPageChange={() => {}}
          loading={studentsLoading}
          loadingMore={loadingMoreStudents}
          serverSide={true}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => openStudentDetail(row)}
          validRowClickTagNames="*"
        />
      </div>

      <div className="att-picklist-foot" ref={studentsEndRef}>
        {loadingMoreStudents ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more students...</span>
          </>
        ) : studentsHasMore ? (
          <span>
            {students.length} of {studentsTotal} students
          </span>
        ) : (
          studentsTotal > 0 && (
            <span>
              All {studentsTotal} student{studentsTotal === 1 ? '' : 's'} loaded
            </span>
          )
        )}
      </div>

      <section className="br-dupes">
          <h5 className="pgm-section-title">Possible duplicate batches</h5>
          <p className="br-dupes-note">
            Grouped by the same course, trainer and name. A heuristic for
            review — nothing here is merged or deleted automatically.
          </p>

          <div className="br-dupes-body">
            {duplicatesLoading && (
              <div className="att-picklist-foot">
                <span className="pg-spinner att-spinner" aria-hidden="true" />
                <span>Looking for duplicates...</span>
              </div>
            )}
            {!duplicatesLoading && duplicates?.length === 0 && (
              <p className="lp-muted">No likely duplicate batches found.</p>
            )}
            {!duplicatesLoading &&
              duplicates?.map((group, idx) => (
                  <article className="br-dupe" key={idx}>
                      <header className="br-dupe-head">
                        <div className="lp-cell">
                          <span className="lp-name">{group.batchName}</span>
                          <span className="lp-sub">
                            {group.courseName} · {group.departmentName}
                          </span>
                        </div>
                        <span className="pg-pill pg-pill--warn">
                          {group.count} batches
                        </span>
                      </header>
                      <div className="br-dupe-table lp-dialog-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Sr. No</th>
                              <th>Branch</th>
                              <th>Classroom</th>
                              <th>Start</th>
                              <th>End</th>
                              <th>Status</th>
                              <th>Overlaps</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.batches.map((b, i) => (
                              <tr key={b.batchId}>
                                <td>{i + 1}</td>
                                <td>{b.branchName || '—'}</td>
                                <td>{b.classroomName || '—'}</td>
                                <td>{formatDate(b.startTime)}</td>
                                <td>{formatDate(b.endTime)}</td>
                                <td>
                                  {b.status === 'ended' ? (
                                    <span className="lp-muted">Ended</span>
                                  ) : (
                                    <span className="pg-pill pg-pill--good">Active</span>
                                  )}
                                </td>
                                <td>
                                  {/* An overlap is the thing worth acting on;
                                      "no" is the quiet, ordinary case. */}
                                  {b.overlapsAnother ? (
                                    <span className="pg-pill pg-pill--bad">Yes</span>
                                  ) : (
                                    <span className="lp-muted">No</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                  </article>
                ))}
          </div>
      </section>

      {/* ── Student detail dialog ─────────────────────────────────────────
          The shell was already the house pg-modal, but everything inside
          it was classless: four nested bare <div>s, bare <label>/<select>
          pairs, a bare <button> and a bare <table>. None of it had a
          border, a ground, padding or a scroller, so the filters stacked
          full width in browser chrome and the eight-column table ran flat
          off the panel.

          It is the page's own vocabulary now -- br-stat for the figures,
          pg-filters for the strip, lp-dialog-table for the table, the
          same pg-pill/lp-muted pair the duplicates list uses for Active
          and Ended -- so the dialog reads as part of the report rather
          than as raw HTML on top of it. */}
      {selectedStudent ? (
        <div
          className="pg-modal"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeStudentDetail();
          }}
        >
          {manageStep === 2 && pickedBatch ? (
            /* ── Step 2: manage the picked batch ────────────────────────
               The same popup, taken over by the one batch. Its edit form
               and its roster are two tabs in the house strip. The edit
               form is the batch list's own embedded AddBatch, so the panel
               takes that form's frame (--xl --form) while it is showing:
               a fixed height, the form's step stage the one scroller. */
            <div
              className={`pg-modal-panel pg-modal-panel--xl${
                manageTab === 'edit' ? ' pg-modal-panel--form' : ''
              }`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="br-manage-title"
            >
              <header className="pg-modal-head">
                <div className="pg-modal-heading">
                  <span className="pg-modal-eyebrow">Manage batch</span>
                  <h3 className="pg-modal-title" id="br-manage-title">
                    {pickedBatch.batchName || 'Unnamed batch'}
                  </h3>
                  <p className="pg-modal-sub">
                    {pickedBatch.courseName || 'No course'}
                    {pickedBatch.branchName ? ` · ${pickedBatch.branchName}` : ''}
                    {' · opened from '}
                    {selectedStudent.studentName || 'this student'}
                  </p>
                </div>
                <button
                  type="button"
                  className="pg-modal-close"
                  onClick={closeStudentDetail}
                  aria-label="Close"
                >
                  &times;
                </button>
              </header>

              {!studentDone && (
              <div className="lp-tabs" role="tablist" aria-label="Manage this batch">
                <button
                  type="button"
                  role="tab"
                  id="br-manage-tab-edit"
                  aria-selected={manageTab === 'edit'}
                  aria-controls="br-manage-panel"
                  className={`lp-tab${manageTab === 'edit' ? ' is-on' : ''}`}
                  onClick={() => setManageTab('edit')}
                >
                  Edit batch
                </button>
                <button
                  type="button"
                  role="tab"
                  id="br-manage-tab-students"
                  aria-selected={manageTab === 'students'}
                  aria-controls="br-manage-panel"
                  className={`lp-tab${manageTab === 'students' ? ' is-on' : ''}`}
                  onClick={() => setManageTab('students')}
                >
                  Students
                </button>
              </div>
              )}

              <div
                className="pg-modal-body"
                role="tabpanel"
                id="br-manage-panel"
                aria-labelledby={manageTab === 'edit' ? 'br-manage-tab-edit' : 'br-manage-tab-students'}
              >
                {studentDone ? (
                  <PopupDone message={studentDone} onClose={closeStudentDetail} />
                ) : manageTab === 'edit' ? (
                  <AddBatch
                    key={pickedBatch.batchId}
                    batchId={pickedBatch.batchId}
                    onCancel={() => setManageStep(1)}
                    onSaved={() => {
                      refreshReport();
                    }}
                  />
                ) : (
                  <BatchStudentsManager batchId={pickedBatch.batchId} onSaved={refreshReport} />
                )}
              </div>

              {/* The edit form carries its own Back / Continue / Save row, so
                  the popup's foot is drawn for the roster tab only. */}
              {manageTab === 'students' && !studentDone && (
                <footer className="pg-modal-foot">
                  <span className="br-stu-note">
                    Changes to the roster are sent when you press Save.
                  </span>
                  <div className="pg-modal-foot-acts">
                    <button type="button" className="pg-btn" onClick={() => setManageStep(1)}>
                      Back
                    </button>
                    <button type="button" className="pg-btn" onClick={closeStudentDetail}>
                      Close
                    </button>
                  </div>
                </footer>
              )}
            </div>
          ) : (
          <div
            className="pg-modal-panel pg-modal-panel--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="br-stu-title"
          >
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Student</span>
                <h3 className="pg-modal-title" id="br-stu-title">
                  {selectedStudent.studentName || 'Unnamed'}
                </h3>
                <p className="pg-modal-sub">
                  {selectedStudent.studentEmail || 'No email on record'}
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={closeStudentDetail}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {studentDone ? (
                <PopupDone message={studentDone} onClose={closeStudentDetail} />
              ) : (
              <>
              {/* The same four figures the page reports, narrowed to this
                  one student. It is why the row was worth opening. */}
              <div className="br-stats br-stu-stats">
                <StatCard label="Batches" value={modalTally.total} />
                <StatCard label="Active" value={modalTally.active} tone="br-stat--good" />
                <StatCard label="Ended" value={modalTally.ended} tone="br-stat--bad" />
                <StatCard
                  label="Departments"
                  value={modalTally.departments}
                  tone={modalTally.departments > 1 ? 'br-stat--warn' : ''}
                />
              </div>

              {/* Filters. Staged here, applied on Apply -- the same
                  contract the page's own filter strip has. */}
              <section className="pg-filters br-stu-filters" aria-label="Filter this student's batches">
                <div className="pg-filters-fields br-fields">
                  <div className="pg-field">
                    <label className="pg-label" htmlFor="br-stu-dept">
                      Department
                    </label>
                    <select
                      id="br-stu-dept"
                      className="pg-select"
                      value={modalDeptId}
                      onChange={(e) => setModalDeptId(e.target.value)}
                    >
                      <option value="">All departments</option>
                      {modalDeptOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pg-field">
                    <label className="pg-label" htmlFor="br-stu-course">
                      Course
                    </label>
                    <select
                      id="br-stu-course"
                      className="pg-select"
                      value={modalCourseId}
                      onChange={(e) => setModalCourseId(e.target.value)}
                    >
                      <option value="">All courses</option>
                      {modalCourseOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pg-field">
                    <label className="pg-label" htmlFor="br-stu-branch">
                      Branch
                    </label>
                    <select
                      id="br-stu-branch"
                      className="pg-select"
                      value={modalBranchId}
                      onChange={(e) => setModalBranchId(e.target.value)}
                    >
                      <option value="">All branches</option>
                      {modalBranchOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pg-field">
                    <label className="pg-label" htmlFor="br-stu-status">
                      Batch status
                    </label>
                    <select
                      id="br-stu-status"
                      className="pg-select"
                      value={modalStatus}
                      onChange={(e) => setModalStatus(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="active">Active</option>
                      <option value="ended">Ended</option>
                    </select>
                  </div>
                </div>

                <div className="pg-filters-actions br-stu-acts">
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={handleModalReset}
                    disabled={!modalFiltersDirty}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="pg-btn pg-btn--main"
                    onClick={handleModalSearch}
                  >
                    Apply
                  </button>
                </div>
              </section>

              <div className="br-stu-list">
                <h5 className="pgm-section-title">
                  Batches
                  <span className="pgm-section-count">{filteredModalBatches.length}</span>
                </h5>

                {canManage && filteredModalBatches.length > 0 && (
                  <p className="pgm-empty">
                    {pickedBatches.length === 0
                      ? 'Pick a batch to manage it, or several to remove this student from all of them.'
                      : pickedBatches.length === 1
                        ? `${pickedBatches[0].batchName || 'This batch'} is picked. Press Manage to edit it or its students.`
                        : `${pickedBatches.length} batches picked. Manage takes one at a time; Remove takes all of them.`}
                  </p>
                )}

                {filteredModalBatches.length > 0 ? (
                  /* The view popup's table (view_popup_rules). With the
                     permission to manage, the row is the control that
                     picks its batch -- click, Enter or Space -- and the
                     checkbox says which ones are picked. Checkboxes rather
                     than a radio because removal takes as many as are
                     ticked; Manage, which edits one batch, is drawn only
                     while exactly one is. */
                  <div className="pgm-tablescroll">
                    <table className="pgm-table pgm-table--values">
                      <thead>
                        <tr>
                          {canManage && (
                            <th className="pgm-num">
                              {/* All of them, meaning all that the filters
                                  left -- never the ones off-screen. */}
                              <input
                                type="checkbox"
                                checked={allFilteredPicked}
                                ref={(el) => {
                                  if (el) el.indeterminate = somePicked && !allFilteredPicked;
                                }}
                                onChange={toggleAllFiltered}
                                aria-label={allFilteredPicked ? 'Clear the picked batches' : 'Pick every batch listed'}
                              />
                            </th>
                          )}
                          <th className="pgm-num">#</th>
                          <th>Batch</th>
                          <th>Department</th>
                          <th>Classroom</th>
                          <th>Runs</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredModalBatches.map((b, i) => {
                          const picked = pickedBatchIds.has(b.batchId);
                          const pick = () => togglePicked(b.batchId);
                          return (
                            <tr
                              key={b.batchId}
                              {...(canManage
                                ? {
                                    className: 'pgm-row--open',
                                    tabIndex: 0,
                                    'aria-selected': picked,
                                    onClick: pick,
                                    onKeyDown: (e) => {
                                      if (e.target !== e.currentTarget) return;
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        pick();
                                      }
                                    },
                                  }
                                : {})}
                            >
                              {canManage && (
                                <td className="pgm-num">
                                  <input
                                    type="checkbox"
                                    checked={picked}
                                    onChange={pick}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={`Pick ${b.batchName || 'this batch'}`}
                                  />
                                </td>
                              )}
                              <td className="pgm-num">{i + 1}</td>
                              <td>
                                <span className="pgm-person-id">
                                  <span className="pgm-person-name">{b.batchName || 'Unnamed batch'}</span>
                                  <span className="pgm-person-mail">{b.courseName || 'No course'}</span>
                                </span>
                              </td>
                              <td>
                                <span className="pgm-person-id">
                                  <span className="pgm-person-name">{b.departmentName || '—'}</span>
                                  <span className="pgm-person-mail">{b.branchName || '—'}</span>
                                </span>
                              </td>
                              <td>
                                {b.classroomName || <span className="lp-muted">—</span>}
                              </td>
                              <td className="pgm-tel">
                                {formatDate(b.startTime)} – {formatDate(b.endTime)}
                              </td>
                              <td>
                                {/* Ended is the ordinary case and stays quiet;
                                    the same pairing the duplicates list uses. */}
                                {b.status === 'ended' ? (
                                  <span className="lp-muted">Ended</span>
                                ) : (
                                  <span className="pg-pill pg-pill--good">Active</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="pg-state">
                    <h3 className="pg-state-title">
                      {modalFiltersApplied ? 'No batches match these filters' : 'No batches'}
                    </h3>
                    <p className="pg-state-text">
                      {modalFiltersApplied
                        ? 'Reset the filters to see all of this student’s batches.'
                        : 'This student is not enrolled in any batch.'}
                    </p>
                  </div>
                )}
              </div>
              </>
              )}
            </div>

            {!studentDone && (
            <footer className="pg-modal-foot">
              <span className="br-stu-note">
                Showing {filteredModalBatches.length} of {modalTally.total} batch
                {modalTally.total === 1 ? '' : 'es'}
              </span>
              <div className="pg-modal-foot-acts">
                <button
                  type="button"
                  className="pg-btn"
                  onClick={closeStudentDetail}
                >
                  Close
                </button>
                {/* Remove takes every picked batch; Manage edits one, so
                    it waits until exactly one is picked. Remove is the
                    destructive one and sits away from the filled button
                    that is the default under the cursor. */}
                {canManage && pickedBatches.length > 0 && (
                  <button
                    type="button"
                    className="pg-btn pg-btn--danger"
                    onClick={removeStudentFromBatch}
                    disabled={removingFromBatch}
                  >
                    {removingFromBatch
                      ? 'Removing…'
                      : pickedBatches.length > 1
                        ? `Remove from ${pickedBatches.length} batches`
                        : 'Remove from batch'}
                  </button>
                )}
                {canManage && pickedBatch && (
                  <button
                    type="button"
                    className="pg-btn pg-btn--main"
                    onClick={() => {
                      setManageTab('edit');
                      setManageStep(2);
                    }}
                  >
                    Manage
                  </button>
                )}
              </div>
            </footer>
            )}
          </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default BatchReportPage;
